import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Box, Button, DialogTitle } from '@mui/material';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { useEffect, useState } from 'react';
import useFormsModalStore from 'src/store/forms';
import { SkeletonForm } from 'src/components/skeleton';
import { useQueryClient } from '@tanstack/react-query';
import { SOCIAL_MANAGER_CHANNEL_LIST } from 'src/constants/queryKeys';
import { networkCreateSocialManagerChannel, networkUpdateSocialManagerChannel } from 'src/network/social-maneger/channel';
import { DialogAnimate } from 'src/components/animate';
import { getDefaultValues } from './defaultValues';
import AccountSelectionTableModal from 'src/sections/@dashboard/social-manager/account/AccountSelectionTableModal';
import { useFetchSocialManagerAccountList } from 'src/network/social-maneger/account';
import { useLocalStorage } from 'src/hooks/useLocalStorage';

const SocialManagerChannelForm = () => {
  const { modal, closeModal } = useFormsModalStore();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [isOpen, setIsOpen] = useState(false);
  const [accountDetails, setAccountDetails] = useState(null);

  const [, setSavedPage] = useLocalStorage('savedPage');

  const [accountsTableData, setAccountsTableData] = useState([]);
  const [currentPageAccountsModal, setCurrentPageAccountsModal] = useState(0);
  const [AccountFilterItemsModalPayload] = useState({ TypeID: 0 });

  const channelDetails = modal?.data;
  const isEditMode = !!channelDetails;

  const { isLoading: accountItemsIsLoading, data: accountItemsData } = useFetchSocialManagerAccountList(
    AccountFilterItemsModalPayload,
    currentPageAccountsModal
  );

  // ---------------------------------------------------------------

  const defaultValues = getDefaultValues(channelDetails, accountDetails?.SocialManagerAccountTypeName);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      SocialManagerChannelSocialManagerAccountID: +accountDetails?.SocialManagerAccountID,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateSocialManagerChannel(channelDetails?.SocialManagerChannelID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('کانال موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [SOCIAL_MANAGER_CHANNEL_LIST] });
          }
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateSocialManagerChannel(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('کانال موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            await queryClient.invalidateQueries({ queryKey: [SOCIAL_MANAGER_CHANNEL_LIST] });
          }
          reset();
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const selectAccountHandler = (item) => {
    setIsOpen(false);
    setAccountDetails(item);
    setValue('SocialManagerChannelSocialManagerAccountID', item?.SocialManagerAccountTypeName);
  };

  useEffect(() => {
    if (accountItemsData) {
      const accountsList = accountItemsData?.data?.items || [];

      setAccountsTableData([...accountsList]);
      setSavedPage({
        pageIndex: currentPageAccountsModal,
        pageName: 'accounts-list',
      });

      const findCurrentAccount = accountsList.find(
        (accountItem) => accountItem?.SocialManagerAccountTypeID === channelDetails?.SocialManagerAccountSocialManagerAccountTypeID
      );

      setAccountDetails(findCurrentAccount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountItemsData]);

  useEffect(() => {
    setValue('SocialManagerChannelSocialManagerAccountID', accountDetails?.SocialManagerAccountTypeName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountDetails]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            {accountItemsIsLoading ? (
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' },
                  width: '100%',
                }}
              >
                <SkeletonForm number={3} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <RHFTextField
                    name="SocialManagerChannelSocialManagerAccountID"
                    type="text"
                    label="انتخاب حساب"
                    InputProps={{
                      endAdornment: (
                        <Button variant="contained" color="inherit" onClick={() => setIsOpen(true)}>
                          انتخاب
                        </Button>
                      ),
                    }}
                  />
                  <RHFTextField name="SocialManagerChannelName" type="text" label="نام کانال" />
                  <RHFTextField name="SocialManagerChannelKey" type="text" label="کلید کانال" />
                </Box>

                <div className="flex items-center gap-4 mt-6">
                  <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                    ذخیره
                  </LoadingButton>
                  <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                    {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                  </LoadingButton>
                  <Button onClick={closeModal} variant="contained" color="error">
                    انصراف
                  </Button>
                </div>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      <DialogAnimate open={isOpen} onClose={() => setIsOpen(false)} maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center', mb: 2 }}>حساب</DialogTitle>
        <div style={{ maxHeight: '80vh' }}>
          <AccountSelectionTableModal
            selectContentHandler={selectAccountHandler}
            setPage={setCurrentPageAccountsModal}
            isLoading={accountItemsIsLoading}
            tableData={accountsTableData}
          />
        </div>
      </DialogAnimate>
    </FormProvider>
  );
};

export default SocialManagerChannelForm;
